<!-- Inline @project #tag input for starting a context from the bottom bar. -->
<template>
  <div class="context-input" ref="wrapper">
    <div class="context-input__row">
      <div class="context-input__entry">
        <span v-if="parser.committedProject.value" class="tag is-small pill-tag pill-project">
          @{{ parser.committedProject.value.name }}
          <button type="button" class="delete is-small" @click="parser.removeProject()"></button>
        </span>
        <span
          v-for="(tag, index) in parser.committedTags.value"
          :key="tag.name"
          class="tag is-small pill-tag pill-tag-item"
        >
          #{{ tag.name }}
          <button type="button" class="delete is-small" @click="parser.removeTag(index)"></button>
        </span>
        <input
          ref="inputEl"
          type="text"
          v-model="parser.inputValue.value"
          class="context-input__field"
          placeholder="@project or #tag..."
          @keydown="handleKeydown"
          @input="parser.onInput()"
          @blur="onBlur"
        >
        <AutocompleteDropdown
          :visible="parser.showAutocomplete.value"
          :items="parser.autocompleteItems.value"
          :trigger-char="parser.autocompleteType.value === 'project' ? '@' : '#'"
          :query="parser.currentTokenText.value"
          :show-create="parser.showCreateOption.value"
          :selected-index="parser.selectedAutocompleteIndex.value"
          drop-up
          @select="parser.selectItem($event)"
          @create="parser.createFromToken()"
          @hover="parser.selectedAutocompleteIndex.value = $event"
        />
      </div>
      <button
        type="button"
        class="button is-small is-context"
        :class="{ 'is-loading': store.contextLoading }"
        :disabled="!canSubmit"
        @click="submit"
      >
        Start
      </button>
      <button
        type="button"
        class="button is-small is-context is-outlined context-input__cancel"
        @click="$emit('cancel')"
      >
        <span class="icon is-small">
          <FontAwesomeIcon :icon="faXmark" />
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { useOmniboxParser } from '../composables/useOmniboxParser'
import AutocompleteDropdown from './AutocompleteDropdown.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

const emit = defineEmits(['cancel', 'started'])

const store = useBlocksStore()
const parser = useOmniboxParser(store)
const inputEl = ref(null)
const wrapper = ref(null)

const canSubmit = computed(() => {
  return parser.committedProject.value || parser.committedTags.value.length > 0
})

function handleKeydown(e) {
  parser.onKeydown(e)
  if (e.defaultPrevented) return

  if (e.key === 'Enter') {
    e.preventDefault()
    submit()
  }
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('cancel')
  }
}

function onBlur() {
  setTimeout(() => {
    if (!wrapper.value?.contains(document.activeElement)) {
      parser.cancelToken({ removeFromInput: true })
    }
  }, 150)
}

async function submit() {
  parser.finalizeActiveToken()

  await nextTick()

  if (!canSubmit.value) return

  const payload = parser.getPayload()
  try {
    await store.startContext({
      projectId: payload.projectId,
      projectName: payload.projectName,
      tagIds: payload.tagIds,
      tagNames: payload.tagNames,
    })
    parser.reset()
    emit('started')
  } catch {
    // error handled in store
  }
}

onMounted(() => {
  inputEl.value?.focus()
})
</script>

<style scoped>
.context-input {
  width: 100%;
}

.context-input__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.context-input__entry {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  position: relative;
}

.context-input__field {
  flex: 1 1 10rem;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0.25em 0;
  font-size: 0.85rem;
  color: var(--bulma-text);
}

.context-input__field::placeholder {
  color: var(--bulma-text-weak);
}

.context-input__cancel {
  flex-shrink: 0;
}

.pill-tag {
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.8rem;
  box-shadow: none;
}

.pill-tag .delete {
  background: rgba(0, 0, 0, 0.28);
}

.pill-project {
  background: rgba(213, 99, 45, 0.14);
  border-color: rgba(213, 99, 45, 0.18);
  color: hsla(19deg, 67%, 26%, 1);
}

.pill-tag-item {
  background: rgba(255, 255, 255, 0.72);
  border-color: rgba(0, 0, 0, 0.08);
  color: rgba(54, 54, 54, 0.92);
}
</style>
